import { TEXT_CASE_OPTIONS, type ResolvedFilters } from "./filters";

/**
 * Parsing the wire syntax back into structured data.
 *
 * Two layers live here:
 * - The legacy slot pair `parseMergeTagSlot` / `formatMergeTagSlot` (default +
 *   capfirst only), kept byte-for-byte compatible because `MergeTagNode` still
 *   round-trips through them until Stack 3 migrates it to a filters map.
 * - The forward-looking `parse(rawInside)` that returns a {@link ResolvedFilters}
 *   map for the new chip popover.
 */

/**
 * Regex that matches any `{{identifier}}` pattern. Used by the Lexical
 * deserializer to find merge tags in plain text.
 */
export const MERGE_TAG_PATTERN = /\{\{\s*([^{}]+?)\s*\}\}/g;

/**
 * Charset for a DTL identifier: word chars, dots, array-index brackets, and
 * hyphens. Hyphens matter for connector metadata ids
 * (`customer.metadata.yotpo-loyalty__tier`); brackets for array access
 * (`tile_data.magic_items[0].name`). Shared by every pattern below and by
 * `detection.ts` so the three copies never drift.
 */
export const MERGE_TAG_IDENTIFIER_CHARSET = String.raw`[\w.\-[\]]`;

const MERGE_TAG_IF_CONDITION = String.raw`${MERGE_TAG_IDENTIFIER_CHARSET}+(?:\s+and\s+${MERGE_TAG_IDENTIFIER_CHARSET}+)*`;

export const MERGE_TAG_IF_ELSE_PATTERN = new RegExp(
  String.raw`\{%\s*if\s+${MERGE_TAG_IF_CONDITION}\s*%\}\{\{\s*([^{}]+?)\s*\}\}\{%\s*else\s*%\}([\s\S]*?)\{%\s*endif\s*%\}`,
);

const MERGE_TAG_IF_OPEN_PATTERN = new RegExp(
  String.raw`^\{%\s*if\s+(${MERGE_TAG_IF_CONDITION})\s*%\}`,
);

const BARE_MERGE_TAG_PATTERN = new RegExp(MERGE_TAG_PATTERN.source, "g");
const IF_ELSE_MERGE_TAG_PATTERN = new RegExp(
  MERGE_TAG_IF_ELSE_PATTERN.source,
  "g",
);

/** Splits the inner contents of a slot into `identifier` + optional filter chain. */
export const MERGE_TAG_SLOT_PATTERN = new RegExp(
  String.raw`^(${MERGE_TAG_IDENTIFIER_CHARSET}+)(?:\s*\|(.+))?$`,
);

const MERGE_TAG_DEFAULT_FILTER_PATTERN = /^default\s*:\s*"([^"]*)"$/;

export interface ParsedMergeTagSlot {
  identifier: string;
  defaultValue?: string;
  textCase?: string;
  /** Unmodeled DTL filters preserved verbatim (e.g. `floatformat:2`). */
  passthrough?: string[];
}

const MERGE_TAG_TRUNCATECHARS_FILTER_PATTERN = /^truncatechars\s*:\s*\d+$/;

/**
 * Renderer-verified filters we don't model in the popover, preserved verbatim
 * so email-dialect tags like `{{ items.0.price|floatformat:2 }}` round-trip
 * through chips. Only names from the verified senderer/gonja set (see
 * README "How to add a filter") — anything else still rejects the tag, since
 * an unknown filter is a gonja parse error that blanks the whole render.
 */
const MERGE_TAG_PASSTHROUGH_FILTER_PATTERN = /^(\w+)(?::[^|{}]*)?$/;
const PASSTHROUGH_FILTER_NAMES = new Set([
  "floatformat",
  "intcomma",
  "slice",
  "truncatewords",
  "truncate",
  "date",
]);

function isPassthroughFilter(filter: string): boolean {
  const name = MERGE_TAG_PASSTHROUGH_FILTER_PATTERN.exec(filter)?.[1];
  if (!name) return false;
  return (
    PASSTHROUGH_FILTER_NAMES.has(name) || name.startsWith("currencyseparator")
  );
}

const TEXT_CASE_FILTER_NAMES = new Set(
  TEXT_CASE_OPTIONS.map((o) => o.id).filter((id) => id !== ""),
);

function parseMergeTagFilters(filterPart: string): {
  defaultValue?: string;
  textCase?: string;
  passthrough?: string[];
} | null {
  let defaultValue: string | undefined;
  let textCase: string | undefined;
  let passthrough: string[] | undefined;

  for (const rawFilter of filterPart.split("|")) {
    const filter = rawFilter.trim();
    if (filter.length === 0) continue;

    if (TEXT_CASE_FILTER_NAMES.has(filter)) {
      textCase = filter;
      continue;
    }

    const defaultMatch = MERGE_TAG_DEFAULT_FILTER_PATTERN.exec(filter);
    if (defaultMatch) {
      defaultValue = defaultMatch[1];
      continue;
    }

    // Deliberately dropped, not passed through: the renderer never supported it.
    if (MERGE_TAG_TRUNCATECHARS_FILTER_PATTERN.test(filter)) continue;

    if (isPassthroughFilter(filter)) {
      (passthrough ??= []).push(filter);
      continue;
    }

    return null;
  }

  return { defaultValue, textCase, passthrough };
}

export function parseMergeTagSlot(
  rawInside: string,
): ParsedMergeTagSlot | null {
  const match = MERGE_TAG_SLOT_PATTERN.exec(rawInside.trim());
  if (!match) return null;

  const identifier = match[1];
  const filterPart = match[2]?.trim();

  if (!filterPart) {
    return { identifier };
  }

  const filters = parseMergeTagFilters(filterPart);
  if (!filters) return null;

  return {
    identifier,
    defaultValue: filters.defaultValue,
    textCase: filters.textCase,
    ...(filters.passthrough?.length
      ? { passthrough: filters.passthrough }
      : {}),
  };
}

/**
 * Builds the body-text representation of a merge tag, optionally appending
 * `|capfirst` and/or `|default:"..."` filters. Inverse of
 * {@link parseMergeTagSlot}.
 *
 * NOTE: this legacy formatter emits `capfirst` before `default` to preserve the
 * exact `MergeTagNode` wire format that ships today. The forward-looking
 * `build()` (see `build.ts`) uses the corrected `default`-before-`capfirst` order.
 */
export function formatMergeTagSlot(
  identifier: string,
  defaultValue?: string,
  capfirst?: boolean,
): string {
  const filters: string[] = [];
  if (capfirst) {
    filters.push("capfirst");
  }
  if (defaultValue !== undefined && defaultValue.length > 0) {
    filters.push(`default:"${defaultValue}"`);
  }

  if (filters.length === 0) {
    return `{{${identifier}}}`;
  }

  return `{{${identifier}|${filters.join("|")}}}`;
}

export function parse(
  rawInside: string,
): { identifier: string; filters: ResolvedFilters } | null {
  const slot = parseMergeTagSlot(rawInside);
  if (!slot) return null;

  const filters: ResolvedFilters = {};
  if (slot.defaultValue !== undefined) filters.default = slot.defaultValue;
  if (slot.textCase !== undefined) filters.textCase = slot.textCase;
  if (slot.passthrough !== undefined) filters.passthrough = slot.passthrough;

  // parseMergeTagSlot never yields the legacy `capfirst`/`truncatechars` keys
  // that normalize() migrates — so no normalize here.
  return { identifier: slot.identifier, filters };
}

export interface MergeTagOccurrence {
  index: number;
  length: number;
  identifier: string;
  filters: ResolvedFilters;
}

function parseIfElseFilters(
  wire: string,
  identifier: string,
  slotFilters: ResolvedFilters,
  elseBranch: string,
): ResolvedFilters {
  const filters = { ...slotFilters };
  const condition = MERGE_TAG_IF_OPEN_PATTERN.exec(wire)?.[1];
  const terminalCondition = condition?.split(/\s+and\s+/).at(-1);

  // Parent-only conditions are safety guards for nested tile_data access, not
  // user-configured fallbacks. A non-empty else is always a real fallback.
  // An empty else is only a configured empty default when the terminal
  // identifier itself is guarded — never overwrite an inner `|default:"…"`.
  if (elseBranch.length > 0) {
    filters.default = elseBranch;
  } else if (
    slotFilters.default === undefined &&
    terminalCondition === identifier
  ) {
    filters.default = elseBranch;
  }

  return filters;
}

export function matchFirstMergeTag(text: string): MergeTagOccurrence | null {
  // Skip past slots that don't parse (e.g. an unsupported filter) so a bad
  // tag early in the string doesn't hide a valid one after it. The if/else
  // search skips too: otherwise a later valid wrapper would only be matched
  // by its inner bare slot, leaving the wrapper behind as text.
  const ifElse = ((): MergeTagOccurrence | null => {
    for (const ifElseMatch of text.matchAll(IF_ELSE_MERGE_TAG_PATTERN)) {
      const slot = parse(ifElseMatch[1]);
      if (!slot) continue;
      return {
        index: ifElseMatch.index,
        length: ifElseMatch[0].length,
        identifier: slot.identifier,
        filters: parseIfElseFilters(
          ifElseMatch[0],
          slot.identifier,
          slot.filters,
          ifElseMatch[2],
        ),
      };
    }
    return null;
  })();

  const bare = ((): MergeTagOccurrence | null => {
    for (const bareMatch of text.matchAll(BARE_MERGE_TAG_PATTERN)) {
      const slot = parse(bareMatch[1]);
      if (!slot) continue;
      return {
        index: bareMatch.index,
        length: bareMatch[0].length,
        identifier: slot.identifier,
        filters: slot.filters,
      };
    }
    return null;
  })();

  if (ifElse && bare) return ifElse.index <= bare.index ? ifElse : bare;
  return ifElse ?? bare;
}
